import type { FieldErrors, UseFormSetError, UseFormSetFocus } from 'react-hook-form';
import {
  A2_DISEASE_OTHER_ID,
  INCIDENT_DATE_TYPE_ID,
  R2_JOINT_TO_BODY_REGION,
} from '@/constants/consentForm';
import { ConsentFormData } from '@/schemas/consentSchema';
import { joinR2Csv } from '@/utils';
import { convGtJDate, dateValueToIso } from '@/utils/dateConverter';
import { parseDate, parseFormat } from '@/components/organisms/RA2NDatePicker/datepickerLogic';
import { JA_JP } from '@/components/organisms/RA2NDatePicker/constants';

const yearFormat = parseFormat(JA_JP.formatYear);
const monthFormat = parseFormat(JA_JP.formatMonth);

const ensureStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter(v => v != null).map(String);
  if (value == null) return [];
  return [String(value)];
};

function isR2SymptomTreatmentMismatch(data: ConsentFormData): boolean {
  const massageParts = ensureStringArray(data.massage_parts);
  const symptomMuscleParts = ensureStringArray(data.symptom_muscle);
  if (massageParts.some(part => !symptomMuscleParts.includes(part))) {
    return true;
  }

  const treatmentParts = new Set([...massageParts, ...ensureStringArray(data.orthomorphia_parts)]);
  if (treatmentParts.size === 0) return false;

  const symptomTargets = new Set<string>();
  symptomMuscleParts.forEach(id => {
    symptomTargets.add(id);
  });

  ensureStringArray(data.symptom_joint).forEach(id => {
    const bodyRegion = R2_JOINT_TO_BODY_REGION[id];
    if (bodyRegion) symptomTargets.add(bodyRegion);
  });

  if (symptomTargets.size === 0) return false;
  return (
    Array.from(symptomTargets).some(target => !treatmentParts.has(target)) ||
    Array.from(treatmentParts).some(part => !symptomTargets.has(part))
  );
}

function calculateOneMonthInclusiveEnd(base: Date): Date {
  const baseDay = base.getDate();
  const targetMonth = new Date(base.getFullYear(), base.getMonth() + 1, 1);
  const daysInTargetMonth = new Date(
    targetMonth.getFullYear(),
    targetMonth.getMonth() + 1,
    0
  ).getDate();

  if (baseDay > daysInTargetMonth) {
    return new Date(targetMonth.getFullYear(), targetMonth.getMonth(), daysInTargetMonth);
  }

  return new Date(targetMonth.getFullYear(), targetMonth.getMonth(), baseDay - 1);
}

function parseConsentDate(value?: string | null): Date | null {
  if (!value) return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match) {
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function calcMaxTreatmentTermTo(
  data: ConsentFormData,
  /** 継続・特殊継続（ルートまたは is_continuation / is_special）のとき true */
  continuationLike: boolean,
  serviceType: 'r2' | 'a2'
): string | null {
  const consent = parseConsentDate(data.consent_date);
  const first = parseConsentDate(data.first_treatment_date);

  // base date:
  // - continue / special continue: consent_date
  // - new: first_treatment_date (with edge-case using consent_date)
  let base: Date | null = null;
  if (continuationLike) {
    base = consent;
  } else {
    base = first;
    if (first && consent) {
      const consentPlus1Month = new Date(consent);
      consentPlus1Month.setMonth(consentPlus1Month.getMonth() + 1);
      consentPlus1Month.setDate(consentPlus1Month.getDate() - 1);
      if (first.getTime() > consentPlus1Month.getTime()) {
        base = consent;
      }
    }
  }

  if (!base) return null;

  const day = base.getDate();
  const hasOrthomorphia = (data.orthomorphia_parts ?? []).length > 0;
  const isR2Orthomorphia = serviceType === 'r2' && hasOrthomorphia;
  if (isR2Orthomorphia) {
    const term = calculateOneMonthInclusiveEnd(base);
    const y = term.getFullYear();
    const m = String(term.getMonth() + 1).padStart(2, '0');
    const d = String(term.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  const monthsToAdd = day <= 15 ? 5 : 6;

  const term = new Date(base);
  term.setMonth(term.getMonth() + monthsToAdd + 1, 0); // endOfMonth after adding months

  const y = term.getFullYear();
  const m = String(term.getMonth() + 1).padStart(2, '0');
  const d = String(term.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export type ConsentPayloadOptions = {
  isContinueRoute: boolean;
  isSpecialContinue: boolean;
};

export type ConsentCrossFieldContext = {
  isContinueRoute: boolean;
  serviceType: 'r2' | 'a2';
  setError: UseFormSetError<ConsentFormData>;
  confirmFn: (message: string) => Promise<boolean>;
  /** Edit-only: true when visits exist within the consent's treatment period (from API). */
  hasVisitsInTreatmentPeriod?: boolean;
  /** Edit-only: initial treatment_content value before user edits. */
  initialTreatmentContent?: unknown;
};

/**
 * Client-side cross-field checks (warnings + blocking) aligned with CreateConsentPage.
 * Returns true when the submit should proceed to API payload build.
 */
export async function runConsentCrossFieldChecks(
  data: ConsentFormData,
  ctx: ConsentCrossFieldContext
): Promise<boolean> {
  const { isContinueRoute, setError, confirmFn } = ctx;

  // 継続・特殊継続（ルートまたはフォームフラグ）を統合判定
  const continuationLike = isContinueRoute || !!data.is_continuation || !!data.is_special;

  // Required/ordering of treatment term is already enforced by Zod schema,
  // but we add the "max date" business rule here because it depends on other fields.
  if (data.treatment_term_to) {
    const maxDate = calcMaxTreatmentTermTo(data, continuationLike, ctx.serviceType);
    if (maxDate) {
      const inputTo = parseConsentDate(data.treatment_term_to);
      const maxTo = parseConsentDate(maxDate);
      if (inputTo && maxTo && inputTo.getTime() > maxTo.getTime()) {
        const maxDateJp = convGtJDate(maxDate) || maxDate;
        setError('treatment_term_to', {
          type: 'manual',
          message: `${maxDateJp}より未来を指定することは出来ません。`,
        });
        return false;
      }
    }
  }

  if (
    data.examine_date &&
    data.consent_date &&
    new Date(data.examine_date) > new Date(data.consent_date)
  ) {
    if (
      !(await confirmFn(
        '診察年月日が同意日より後の日付になっていますが、その内容で登録して良いですか？'
      ))
    ) {
      return false;
    }
  }

  if (
    data.incident_date_type_id !== INCIDENT_DATE_TYPE_ID.UNKNOWN &&
    data.incident_date &&
    data.first_treatment_date &&
    new Date(
      normalizeIncidentDate(data.incident_date, data.incident_date_type_id) ?? data.incident_date
    ) > new Date(dateValueToIso(data.first_treatment_date) ?? data.first_treatment_date)
  ) {
    if (
      !(await confirmFn(
        '発病年月日が初療年月日より後の日付になっていますが、その内容で登録して良いですか？'
      ))
    ) {
      return false;
    }
  }

  if (
    data.consent_date &&
    data.treatment_term_from &&
    new Date(data.consent_date) > new Date(data.treatment_term_from)
  ) {
    if (
      !(await confirmFn('要加療期間内で同意日より前の施術は申請対象外となります。ご注意ください。'))
    ) {
      return false;
    }
  }

  if (
    !continuationLike &&
    data.first_treatment_date &&
    data.consent_date &&
    new Date(data.first_treatment_date) < new Date(data.consent_date)
  ) {
    setError('first_treatment_date', {
      type: 'manual',
      message: '同意を得てからの施術が申請対象です。初療日は同意日以降の日付を入力してください。',
    });
    return false;
  }

  if (ctx.serviceType === 'r2' && isR2SymptomTreatmentMismatch(data)) {
    if (
      !(await confirmFn(
        '症状欄と施術部位が異なった場合、症状その他欄に施術理由を追記する必要があります。'
      ))
    ) {
      return false;
    }
  }

  // Edit-only: warn when visits exist in the treatment period (RA2 No1123).
  if (ctx.hasVisitsInTreatmentPeriod) {
    const sortedJson = (v: unknown) => JSON.stringify(Array.isArray(v) ? [...v].sort() : v);
    const treatmentContentChanged =
      sortedJson(ctx.initialTreatmentContent) !== sortedJson(data.treatment_content);

    if (treatmentContentChanged) {
      if (
        !(await confirmFn('要加療期間内の施術データの施術内容が上書きされますが、よろしいですか？'))
      ) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Normalize incident_date to match the selected incident_date_type_id:
 *   UNKNOWN → null
 *   YEAR    → YYYY-01-01  (only year component is meaningful)
 *   MONTH   → YYYY-MM-01  (only year+month are meaningful)
 *   DATE    → YYYY-MM-DD  (full date)
 *
 * This is a safety-net for data loaded from the API that may not have been
 * normalized at entry time (e.g., a 年指定 record stored with a non-01 month/day).
 */
function normalizeIncidentDate(
  date: string | null | undefined,
  typeId: number | null | undefined
): string | null {
  if (!date || typeId === INCIDENT_DATE_TYPE_ID.UNKNOWN || !typeId) return null;

  // Already ISO format (YYYY-MM-DD)
  const ymd = date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymd) {
    const [, y, m] = ymd;
    if (typeId === INCIDENT_DATE_TYPE_ID.YEAR) return `${y}-01-01`;
    if (typeId === INCIDENT_DATE_TYPE_ID.MONTH) return `${y}-${m}-01`;
    return date;
  }

  // Year picker format (e.g. '2026年' or '平成25年')
  if (typeId === INCIDENT_DATE_TYPE_ID.YEAR) {
    const parsed = parseDate(date, yearFormat);
    if (!Number.isNaN(parsed.getTime())) {
      return `${parsed.getFullYear()}-01-01`;
    }
    return null;
  }

  // Month picker format (e.g. '2026年04月' or '平成25年03月')
  if (typeId === INCIDENT_DATE_TYPE_ID.MONTH) {
    const parsed = parseDate(date, monthFormat);
    if (!Number.isNaN(parsed.getTime())) {
      return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-01`;
    }
    return null;
  }

  // Day picker: use dateValueToIso for 和暦 format
  const iso = dateValueToIso(date);
  return iso || null;
}

export function buildA2ConsentRequestBody(
  data: ConsentFormData,
  opts: ConsentPayloadOptions
): Record<string, unknown> {
  const nonPrintCols = data.non_print_cols ?? [];
  const selectedDiseaseIds = Array.isArray(data.disease_name)
    ? data.disease_name.filter(v => v != null).map(String)
    : [];
  const hasOther = selectedDiseaseIds.includes(A2_DISEASE_OTHER_ID);

  return {
    ...data,
    disease_name: selectedDiseaseIds,
    disease_name_other: hasOther ? data.disease_name_other : null,
    non_print_cols: nonPrintCols,
    is_continuation: data.is_continuation || opts.isContinueRoute,
    is_special: data.is_special ?? opts.isSpecialContinue,
    incident_date: normalizeIncidentDate(data.incident_date, data.incident_date_type_id),
  };
}

export function buildR2ConsentRequestBody(
  data: ConsentFormData,
  opts: ConsentPayloadOptions
): Record<string, unknown> {
  const nonPrintCols = data.non_print_cols ?? [];
  const mp = data.massage_parts ?? [];
  const payload: Record<string, unknown> = {
    ...data,
    disease_name: (data.disease_name_r2 ?? '').trim(),
    symptom_muscle: joinR2Csv(data.symptom_muscle),
    symptom_joint: joinR2Csv(data.symptom_joint),
    symptom_other: data.symptom_other ?? '',
    symptom_joint_other: data.symptom_joint_other ?? '',
    is_symptom_print: data.is_symptom_print,
    massage: joinR2Csv(mp),
    orthomorphia: joinR2Csv(data.orthomorphia_parts),
    hot_treatment_type_id: mp.length > 0 ? data.hot_treatment_type_id : null,
    non_print_cols: nonPrintCols,
    is_continuation: data.is_continuation || opts.isContinueRoute,
    is_special: data.is_special ?? opts.isSpecialContinue,
  };

  delete payload.disease_name_r2;
  delete payload.massage_parts;
  delete payload.orthomorphia_parts;
  delete payload.treatment_content;
  delete payload.disease_name_other;

  payload.incident_date = normalizeIncidentDate(data.incident_date, data.incident_date_type_id);

  return payload;
}

/**
 * Map Laravel-style validation errors onto RHF fields (422).
 */
export function applyConsentApiFieldErrors(
  error: unknown,
  setError: UseFormSetError<ConsentFormData>
): void {
  const err = error as {
    response?: { status?: number; data?: { errors?: Record<string, string[]> } };
  };
  if (err.response?.status === 422 && err.response.data?.errors) {
    const apiErrors = err.response.data.errors;
    Object.keys(apiErrors).forEach(key => {
      setError(key as keyof ConsentFormData & string, {
        type: 'manual',
        message: Array.isArray(apiErrors[key]) ? apiErrors[key][0] : String(apiErrors[key]),
      });
    });
  }
}

export function makeConsentInvalidHandler(
  toast: { error: (message: string) => void },
  setFocus: UseFormSetFocus<ConsentFormData>
) {
  return (invalid: FieldErrors<ConsentFormData>) => {
    const keys = Object.keys(invalid) as Array<keyof ConsentFormData>;
    const firstKey = keys[0];
    toast.error('更新ができませんでした。');

    if (firstKey) setFocus(firstKey);
  };
}
